import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { accord, pluriel } from '@/lib/pluriel';
import { AdminButton } from '@/components/admin/button';
import { Card, CardBody, CardHead } from '@/components/admin/card';
import { AToggle, FormRow } from '@/components/admin/form';
import { AdminIcon } from '@/components/admin/icon';
import { PageHead } from '@/components/admin/page-head';
import { Tag } from '@/components/admin/tag';
import { useToast } from '@/components/ui/feedback';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import { usePageTitle } from '@/hooks/use-page-title';
import {
  socialApi,
  type CreateSocialPostInput,
  type SocialPlatform,
  type SocialPost,
  type SocialPostStatus,
} from '@/lib/social/social-api';
import type { TagClass } from '@/lib/status';

const STATUS_META: Record<SocialPostStatus, { label: string; cls: TagClass }> = {
  draft: { label: 'Brouillon', cls: 'neutral' },
  scheduled: { label: 'Programmé', cls: 'info' },
  publishing: { label: 'Publication…', cls: 'warning' },
  published: { label: 'Publié', cls: 'success' },
  failed: { label: 'Échec', cls: 'danger' },
};

const PLATFORM_LABEL: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
};

export function AdminSocialPage() {
  usePageTitle('Réseaux sociaux');
  const toast = useToast();
  const qc = useQueryClient();

  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['facebook']);
  const [message, setMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [context, setContext] = useState('');
  const [tone, setTone] = useState<'promo' | 'nouveaute' | 'reassort' | 'conseil'>('nouveaute');
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');

  const status = useQuery({ queryKey: ['social', 'status'], queryFn: () => socialApi.status() });
  const posts = useQuery({ queryKey: ['social', 'posts'], queryFn: () => socialApi.list() });

  const togglePlatform = (p: SocialPlatform) =>
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const generate = useMutation({
    mutationFn: () =>
      socialApi.generateCaption({ context: context || message, platform: platforms[0], tone }),
    onSuccess: (r) => {
      setMessage(r.caption);
      toast.success('Légende générée par l’IA.');
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const buildInput = (withSchedule: boolean): CreateSocialPostInput => ({
    platforms,
    message: message.trim(),
    ...(mediaUrl.trim() ? { mediaUrl: mediaUrl.trim() } : {}),
    ...(linkUrl.trim() ? { linkUrl: linkUrl.trim() } : {}),
    ...(withSchedule && scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
  });

  const resetForm = () => {
    setMessage('');
    setMediaUrl('');
    setLinkUrl('');
    setContext('');
    setScheduleOn(false);
    setScheduledAt('');
    void qc.invalidateQueries({ queryKey: ['social', 'posts'] });
  };

  const saveDraft = useMutation({
    mutationFn: () => socialApi.create(buildInput(false)),
    onSuccess: () => {
      toast.success('Brouillon enregistré.');
      resetForm();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const schedule = useMutation({
    mutationFn: () => socialApi.create(buildInput(true)),
    onSuccess: () => {
      toast.success('Publication programmée.');
      resetForm();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const publishNow = useMutation({
    mutationFn: async () => {
      const created = await socialApi.create(buildInput(false));
      return Promise.all(created.map((p) => socialApi.publish(p.id)));
    },
    onSuccess: (results) => {
      const ok = results.filter((r) => r.status === 'published').length;
      const failed = results.length - ok;
      if (ok) toast.success(`${pluriel(ok, 'publication')} ${accord(ok, 'envoyée')}.`);
      if (failed) toast.error(`${failed} échec(s) — voir la liste.`);
      resetForm();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const canSubmit = platforms.length > 0 && message.trim().length > 0 && !publishNow.isPending;
  const igNeedsImage = platforms.includes('instagram') && !mediaUrl.trim();
  const busy =
    generate.isPending || saveDraft.isPending || schedule.isPending || publishNow.isPending;

  const statusActions = useMemo(
    () => (
      <div style={{ display: 'flex', gap: 8 }}>
        <Tag variant={status.data?.facebook ? 'success' : 'neutral'}>
          Facebook {status.data?.facebook ? 'connecté' : 'non configuré'}
        </Tag>
        <Tag variant={status.data?.instagram ? 'success' : 'neutral'}>
          Instagram {status.data?.instagram ? 'connecté' : 'non configuré'}
        </Tag>
      </div>
    ),
    [status.data],
  );

  return (
    <>
      <PageHead
        title="Réseaux sociaux"
        sub="Composer, générer avec l’IA, programmer et suivre vos publications Facebook & Instagram."
        actions={statusActions}
      />

      <Card style={{ marginBottom: 18 }}>
        <CardHead
          title="Nouvelle publication"
          sub="Rédigez ou générez la légende, puis publiez ou programmez."
        />
        <CardBody>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 20,
            }}
          >
            <div>
              <FormRow label="Message" required helper={`${message.length}/2200`}>
                <textarea
                  id="social-msg"
                  className="inp"
                  rows={7}
                  value={message}
                  maxLength={2200}
                  placeholder="Rédigez votre publication, ou générez-la avec l’IA ci-dessous."
                  onChange={(e) => setMessage(e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              </FormRow>

              <div
                style={{
                  background: 'var(--a-surface-2)',
                  border: '1px solid var(--a-border)',
                  borderRadius: 'var(--a-r)',
                  padding: '12px 14px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--a-ink-2)',
                  }}
                >
                  <span style={{ color: 'var(--a-terracotta)', display: 'inline-flex' }}>
                    <AdminIcon n="star" s={13} />
                  </span>
                  Assistant IA
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    className="inp"
                    style={{ flex: 1, minWidth: 200 }}
                    placeholder="Contexte / produit (ex. Téléphone Infinix Hot 40)"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                  />
                  <select
                    className="inp"
                    style={{ width: 130, flex: 'none' }}
                    value={tone}
                    onChange={(e) => setTone(e.target.value as typeof tone)}
                  >
                    <option value="nouveaute">Nouveauté</option>
                    <option value="promo">Promo</option>
                    <option value="reassort">Réassort</option>
                    <option value="conseil">Conseil</option>
                  </select>
                  <AdminButton
                    variant="secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => generate.mutate()}
                  >
                    {generate.isPending ? 'Génération…' : '✨ Générer'}
                  </AdminButton>
                </div>
              </div>
            </div>

            <div>
              <FormRow label="Plateformes" required>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['facebook', 'instagram'] as SocialPlatform[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`chip-filter${platforms.includes(p) ? ' on' : ''}`}
                      onClick={() => togglePlatform(p)}
                      aria-pressed={platforms.includes(p)}
                    >
                      {PLATFORM_LABEL[p]}
                    </button>
                  ))}
                </div>
              </FormRow>

              <FormRow
                label="Image (URL)"
                helper={igNeedsImage ? 'Requise pour Instagram' : undefined}
              >
                <input
                  id="social-media"
                  className="inp"
                  placeholder="https://…/image.jpg"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  style={igNeedsImage ? { borderColor: 'var(--a-danger)' } : undefined}
                />
              </FormRow>

              <FormRow label="Lien (Facebook)">
                <input
                  id="social-link"
                  className="inp"
                  placeholder="https://akwaba.cm/…"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </FormRow>

              <FormRow label="Programmation">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <AToggle on={scheduleOn} onChange={setScheduleOn} />
                  <span style={{ fontSize: 12.5, color: 'var(--a-ink-soft)' }}>
                    Programmer pour plus tard
                  </span>
                  {scheduleOn && (
                    <input
                      type="datetime-local"
                      className="inp"
                      style={{ maxWidth: 220 }}
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                    />
                  )}
                </div>
              </FormRow>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 16,
              paddingTop: 14,
              borderTop: '1px solid var(--a-border)',
            }}
          >
            <AdminButton
              variant="ghost"
              type="button"
              disabled={!canSubmit || busy}
              onClick={() => saveDraft.mutate()}
            >
              Enregistrer en brouillon
            </AdminButton>
            {scheduleOn ? (
              <AdminButton
                variant="primary"
                type="button"
                disabled={!canSubmit || busy || !scheduledAt}
                onClick={() => schedule.mutate()}
              >
                Programmer
              </AdminButton>
            ) : (
              <AdminButton
                variant="primary"
                type="button"
                disabled={!canSubmit || busy}
                onClick={() => publishNow.mutate()}
              >
                {publishNow.isPending ? 'Publication…' : 'Publier maintenant'}
              </AdminButton>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="Publications"
          sub={pluriel(posts.data?.length ?? 0, 'publication')}
        />
        <CardBody>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Plateforme</th>
                  <th>Message</th>
                  <th>Statut</th>
                  <th>Quand</th>
                  <th>Stats</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(posts.data ?? []).map((post) => (
                  <PostRow key={post.id} post={post} />
                ))}
                {posts.data?.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: 'center', padding: 28, color: 'var(--a-ink-mute)' }}
                    >
                      Aucune publication pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function PostRow({ post }: { post: SocialPost }) {
  const toast = useToast();
  const qc = useQueryClient();
  const meta = STATUS_META[post.status];

  const publish = useMutation({
    mutationFn: () => socialApi.publish(post.id),
    onSuccess: (p) => {
      if (p.status === 'published') {
        toast.success('Publié.');
      } else {
        toast.error(p.error ?? 'Échec.');
      }
      void qc.invalidateQueries({ queryKey: ['social', 'posts'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });
  const refresh = useMutation({
    mutationFn: () => socialApi.insights(post.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['social', 'posts'] }),
  });

  const when = post.publishedAt ?? post.scheduledAt;
  return (
    <tr>
      <td>{PLATFORM_LABEL[post.platform]}</td>
      <td style={{ maxWidth: 320 }}>
        <span title={post.message}>
          {post.message.slice(0, 80)}
          {post.message.length > 80 ? '…' : ''}
        </span>
        {post.error && (
          <div style={{ color: 'var(--a-danger)', fontSize: 12 }}>{post.error}</div>
        )}
      </td>
      <td>
        <Tag variant={meta.cls}>{meta.label}</Tag>
      </td>
      <td className="tn" style={{ fontSize: 12.5 }}>
        {when ? new Date(when).toLocaleString('fr-FR') : '—'}
      </td>
      <td className="tn" style={{ fontSize: 12.5 }}>
        {post.insights
          ? Object.entries(post.insights)
              .map(([k, v]) => `${k}:${v}`)
              .join(' · ')
          : '—'}
      </td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {(post.status === 'draft' || post.status === 'failed') && (
          <AdminButton
            variant="ghost"
            size="sm"
            disabled={publish.isPending}
            onClick={() => publish.mutate()}
          >
            Publier
          </AdminButton>
        )}
        {post.status === 'published' && (
          <>
            <AdminButton
              variant="ghost"
              size="sm"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              Stats
            </AdminButton>
            {post.permalink && (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 8, fontSize: 12.5, color: 'var(--a-terracotta)' }}
              >
                Voir
              </a>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
